import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import {
  MANGEL_KATEGORIEN,
  MANGEL_KATEGORIE_LABEL,
  reklamationenApi,
  type MangelKategorie,
} from '../lib/reklamationen-api';
import { requireAccess } from '../lib/guards';

export const Route = createFileRoute('/reklamationen/neu')({
  beforeLoad: () => requireAccess('/api/v1/reklamationen'),
  component: NeueReklamationPage,
});

interface KundenListItem {
  id: string;
  kundennummer: string | null;
  firmenname: string;
}

interface SubunternehmerListItem {
  id: string;
  firmenname: string;
  ansprechpartner: string | null;
}

export function NeueReklamationPage(): JSX.Element {
  const navigate = useNavigate();
  const [kundeId, setKundeId] = useState('');
  const [titel, setTitel] = useState('');
  const [mangelKategorie, setMangelKategorie] = useState<MangelKategorie>('material');
  const [subunternehmerId, setSubunternehmerId] = useState('');
  const [gewaehrleistungBis, setGewaehrleistungBis] = useState('');
  const [beschreibung, setBeschreibung] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'list'],
    queryFn: () => api<{ items: KundenListItem[] }>('/api/v1/kunden'),
  });

  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubunternehmerListItem[] }>('/api/v1/subunternehmer'),
  });

  const create = useMutation({
    mutationFn: () =>
      reklamationenApi.melden({
        kundeId,
        titel: titel.trim(),
        mangelKategorie,
        ...(subunternehmerId.length > 0 ? { subunternehmerId } : {}),
        ...(gewaehrleistungBis.length > 0 ? { gewaehrleistungBis } : {}),
        ...(beschreibung.trim().length > 0 ? { beschreibung: beschreibung.trim() } : {}),
      }),
    onSuccess: ({ item }) => {
      void navigate({ to: '/reklamationen/$id', params: { id: item.id } });
    },
  });

  const canSubmit = kundeId.length > 0 && titel.trim().length > 0 && !create.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Neue Reklamation"
        description="Mangel an einem Subunternehmer-Auftrag melden — Status startet auf Entwurf, geht beim Sub-Zuordnen automatisch auf Bestätigt."
      />

      {create.error ? <ErrorBanner error={create.error as Error} /> : null}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) create.mutate();
        }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Kunde</span>
          <Select value={kundeId} onChange={(e) => setKundeId(e.target.value)} required>
            <option value="" disabled>
              – Kunde wählen –
            </option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer ? `${k.kundennummer} · ` : ''}
                {k.firmenname}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Titel</span>
          <Input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            maxLength={200}
            required
            placeholder="z. B. Heizungs-Reparatur Hauptstr. 12 — Nachbesserung nötig"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Mangel-Kategorie</span>
          <Select
            value={mangelKategorie}
            onChange={(e) => setMangelKategorie(e.target.value as MangelKategorie)}
            required
          >
            {MANGEL_KATEGORIEN.map((k) => (
              <option key={k} value={k}>
                {MANGEL_KATEGORIE_LABEL[k]}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Subunternehmer (optional, kann später zugeordnet werden)
          </span>
          <Select value={subunternehmerId} onChange={(e) => setSubunternehmerId(e.target.value)}>
            <option value="">– Noch nicht zuordnen –</option>
            {(subsQ.data?.items ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.firmenname}
                {s.ansprechpartner ? ` · ${s.ansprechpartner}` : ''}
              </option>
            ))}
          </Select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Gewährleistung bis (optional)
          </span>
          <Input
            type="date"
            value={gewaehrleistungBis}
            onChange={(e) => setGewaehrleistungBis(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Beschreibung (optional)
          </span>
          <textarea
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            rows={4}
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            placeholder="Was wurde mangelhaft ausgeführt? Welche Nachbesserung wird verlangt?"
          />
        </label>

        <div className="flex gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? 'Speichere…' : 'Anlegen'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate({ to: '/reklamationen' })}
          >
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}
